import { playerStats, rangPlayerOfDeal, seatsOfTeam, totals, type MatchState, type TeamId } from '../game/match';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { announce, guaaTallyPhrases, voiceSupported } from '../lib/voice';
import { Avatar, Name, Sheet, teamAvatarFill } from './ui';

interface Props {
  open: boolean;
  onClose: () => void;
  state: MatchState;
  seats: SeatPlayer[];
  teams: Teams;
}

/** Who chose Rang, who was saved, and every deal that gave a Guaa — per team. */
export function GuaaDetailsSheet({ open, onClose, state, seats, teams }: Props) {
  const t = totals(state.deals);
  const players = playerStats(state);
  const leader: TeamId | null = t.guaaA < t.guaaB ? 'A' : t.guaaB < t.guaaA ? 'B' : null;

  const team = (id: TeamId) => {
    const info = teams[id];
    const guaa = id === 'A' ? t.guaaA : t.guaaB;
    const lost = state.deals.filter((d) => d.trumpTeam === id && d.result === 'guaa');
    return (
      <section key={id} className="rounded-2xl ring-1 ring-ink/10" aria-labelledby={`guaa-team-${id}`}>
        <header className="flex items-center gap-2 rounded-t-2xl px-4 py-3" style={{ background: `${info.color}1a` }}>
          <span className="h-3.5 w-3.5 shrink-0 rounded-full" style={{ background: info.color }} aria-hidden />
          <h3 id={`guaa-team-${id}`} className="min-w-0 flex-1 truncate font-bold">
            {info.name}
          </h3>
          {leader === id && <span className="rounded-full bg-success-600 px-2 py-0.5 text-xs font-bold text-white">Leading</span>}
          <span className="font-display text-2xl font-extrabold tabular-nums" style={{ color: info.color }}>
            {guaa}
          </span>
          <span className="text-xs font-bold text-ink-soft uppercase">Guaa</span>
        </header>

        <ul className="divide-y divide-ink/8 px-4">
          {seatsOfTeam(id).map((seat) => {
            const p = seats[seat]!;
            const s = players[seat];
            return (
              <li key={seat} className="flex items-center gap-3 py-3">
                <Avatar name={p.name} avatarKey={p.avatarKey} size={40} ring={info.color} fill={teamAvatarFill(info.color)} />
                <div className="min-w-0 flex-1">
                  <Name className="block truncate font-semibold">{p.name}</Name>
                  <p className="text-sm text-ink-soft">
                    {s.called === 0
                      ? "Hasn't chosen Rang yet"
                      : `Chose Rang ${s.called}× · saved ${s.saved}${s.courts ? ` · ${s.courts} court${s.courts > 1 ? 's' : ''}` : ''}`}
                  </p>
                </div>
                {voiceSupported() && (
                  <button
                    type="button"
                    onClick={() => announce(guaaTallyPhrases(p.name, s.guaa), { force: true })}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-base ring-1 ring-ink/15 hover:bg-ink/5"
                    aria-label={`Say ${p.name}'s Guaa in Hindi`}
                    title="Announce"
                  >
                    🔊
                  </button>
                )}
                <span
                  className={`min-w-11 shrink-0 rounded-full px-2.5 py-1 text-center text-sm font-bold tabular-nums ${
                    s.guaa > 0 ? 'bg-suit-red text-white' : 'bg-success-700/10 text-success-700'
                  }`}
                  aria-label={`${s.guaa} Guaa`}
                >
                  {s.guaa > 0 ? `+${s.guaa}` : '0'}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-ink/10 px-4 py-3">
          <h4 className="eyebrow mb-2 text-ink-soft">Deals that gave Guaa</h4>
          {lost.length === 0 ? (
            <p className="text-sm text-success-700">✓ None, saved every time so far.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {lost.map((d) => {
                const seat = rangPlayerOfDeal(d, state.rules);
                return (
                  <li key={d.dealNo} className="flex items-baseline gap-2">
                    <span className="w-7 shrink-0 font-semibold text-ink-soft tabular-nums">#{d.dealNo}</span>
                    <span className="min-w-0 flex-1">
                      {seat !== null ? <Name className="font-semibold">{seats[seat]!.name}</Name> : 'Their team'} chose Rang, dealt by{' '}
                      <Name>{seats[d.dealerSeat]!.name}</Name>
                    </span>
                    <span className="shrink-0 font-bold whitespace-nowrap text-suit-red">
                      +{d.guaaA + d.guaaB}
                      {d.isCourt && ' Court'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    );
  };

  return (
    <Sheet open={open} onClose={onClose} title="Guaa details">
      <p className="mb-4 text-sm text-ink-soft">
        The player who chose Rang is the one at risk: if their team doesn't make 7 tricks, they get the Guaa.
      </p>
      <div className="space-y-4">
        {team('A')}
        {team('B')}
      </div>
    </Sheet>
  );
}
